/**
 * Read/Write Patterns view (doc 147 §6.6 E2 + PR Q2 drill-down).
 *
 * Per-entity Cloud Function touchpoint counts. PR Q2 adds:
 *  - Row click → DrillDownPanel listing every cloud function that
 *    reads or writes the entity, with proportional frequency bars
 *  - SortableTable replaces the static `<table>` (uses PR F primitives)
 *  - EntityChip in the row replaces the bare button
 */
import { useMemo, useState } from 'react';
import type { AtlasManifest, CloudFunction } from '@atlas-manifest/types';
import { readWritePatterns, type EntityRwPattern } from '../state/readWritePatterns.js';
import { useFocusScroll } from '../state/useFocusScroll.js';
import { SortableTable, type SortableColumn } from '../components/SortableTable.js';
import { DrillDownPanel } from '../components/DrillDownPanel.js';
import { EntityChip } from '../components/EntityChip.js';

export interface ReadWritePatternsViewProps {
  manifest: AtlasManifest;
  onNavigateEntity: (entityId: string) => void;
  /** Optional entity id to scroll to + highlight (from `#patterns/focus/<id>`). */
  focus?: string;
}

const CLASS_LABEL: Record<EntityRwPattern['classification'], { label: string; tone: string }> = {
  'caching-candidate': { label: 'cache · read-heavy', tone: 'warn' },
  'sharding-candidate': { label: 'shard · write-heavy', tone: 'danger' },
  balanced: { label: 'balanced', tone: '' },
  untouched: { label: '—', tone: '' },
};

interface CfBreakdown {
  readers: { name: string; freq: number }[];
  writers: { name: string; freq: number }[];
  maxFreq: number;
}

function breakdownFor(
  manifest: AtlasManifest,
  entityId: string,
): CfBreakdown {
  const readers: { name: string; freq: number }[] = [];
  const writers: { name: string; freq: number }[] = [];
  for (const cf of manifest.cloudFunctions) {
    if (cf.reads.includes(entityId)) readers.push({ name: cf.name, freq: 1 });
    if (cf.writes.includes(entityId)) writers.push({ name: cf.name, freq: 1 });
  }
  // Group by name to collapse duplicates; "freq" defaults to occurrence
  // count from the manifest. When telemetry attaches, the bars rescale
  // automatically against `maxFreq`.
  const groupBy = (rows: { name: string; freq: number }[]): { name: string; freq: number }[] => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(r.name, (m.get(r.name) ?? 0) + r.freq);
    return [...m.entries()].map(([name, freq]) => ({ name, freq })).sort((a, b) => b.freq - a.freq);
  };
  const r = groupBy(readers);
  const w = groupBy(writers);
  const maxFreq = Math.max(1, ...r.map((x) => x.freq), ...w.map((x) => x.freq));
  return { readers: r, writers: w, maxFreq };
}

export function ReadWritePatternsView({
  manifest,
  onNavigateEntity,
  focus,
}: ReadWritePatternsViewProps): React.JSX.Element {
  const rows = useMemo(() => readWritePatterns(manifest), [manifest]);
  const [drillEntity, setDrillEntity] = useState<string | null>(null);
  useFocusScroll(focus);

  const columns: SortableColumn<EntityRwPattern>[] = [
    {
      key: 'entityId',
      label: 'Entity',
      accessor: (r) => r.entityId,
      render: (r) => <EntityChip entityId={r.entityId} manifest={manifest} onClick={onNavigateEntity} />,
    },
    { key: 'reads', label: 'Reads', accessor: (r) => r.reads, mono: true, align: 'right' },
    { key: 'writes', label: 'Writes', accessor: (r) => r.writes, mono: true, align: 'right' },
    {
      key: 'readWriteRatio',
      label: 'R / W',
      accessor: (r) => r.readWriteRatio,
      render: (r) => r.readWriteRatio.toFixed(2),
      mono: true, align: 'right',
    },
    {
      key: 'classification',
      label: 'Hint',
      accessor: (r) => r.classification,
      render: (r) => {
        const c = CLASS_LABEL[r.classification];
        return c.tone ? <span className={`chip ${c.tone}`}>{c.label}</span> : <span className="chip">{c.label}</span>;
      },
    },
  ];

  const drill = drillEntity ? breakdownFor(manifest, drillEntity) : null;
  const drillRow = drillEntity ? rows.find((r) => r.entityId === drillEntity) : undefined;

  return (
    <div className="rw-patterns">
      <h2>Read/Write Patterns</h2>
      <p className="subtitle">
        Per-entity Cloud Function touchpoint counts. Click any row to
        inspect the functions driving the R/W ratio. Read-heavy rows are
        caching candidates; write-heavy rows are sharding candidates.
      </p>
      <SortableTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.entityId}
        initialSort={{ key: 'reads', dir: 'desc' }}
        onRowClick={(r) => setDrillEntity(r.entityId)}
        {...(drillEntity ? { selectedKey: drillEntity } : {})}
      />

      <DrillDownPanel
        open={drillEntity !== null && drill !== null}
        title={drillEntity ? `${drillEntity} · why ${drillRow?.readWriteRatio.toFixed(2) ?? '0'}?` : ''}
        subtitle="Functions touching this entity · graceful fallback active when telemetry missing"
        onClose={() => setDrillEntity(null)}
        footer={
          drillEntity && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => onNavigateEntity(drillEntity)}
            >
              Open {drillEntity}
            </button>
          )
        }
      >
        {drill && (
          <div className="rw-drill">
            <div className="rw-drill-section">
              <span className="rw-drill-label">READERS · {drill.readers.length}</span>
              {drill.readers.length === 0 ? (
                <p className="rw-drill-empty">No readers.</p>
              ) : (
                <ul className="rw-drill-list">
                  {drill.readers.map((r) => (
                    <CfRow key={r.name} name={r.name} freq={r.freq} maxFreq={drill.maxFreq} writer={false} />
                  ))}
                </ul>
              )}
            </div>
            <div className="rw-drill-section">
              <span className="rw-drill-label">WRITERS · {drill.writers.length}</span>
              {drill.writers.length === 0 ? (
                <p className="rw-drill-empty">No writers — read-only entity.</p>
              ) : (
                <ul className="rw-drill-list">
                  {drill.writers.map((w) => (
                    <CfRow key={w.name} name={w.name} freq={w.freq} maxFreq={drill.maxFreq} writer />
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DrillDownPanel>
    </div>
  );
}

function CfRow({
  name, freq, maxFreq, writer,
}: { name: string; freq: number; maxFreq: number; writer: boolean }): React.JSX.Element {
  const pct = Math.max(8, (freq / maxFreq) * 100);
  return (
    <li className="rw-drill-row">
      <span className="mono rw-drill-name">{name}</span>
      <div className="rw-drill-bar-wrap">
        <div
          className="rw-drill-bar"
          style={{ width: pct + '%', background: writer ? 'var(--warn)' : 'var(--brand)' }}
        />
      </div>
      <span className="mono rw-drill-freq">{freq}</span>
    </li>
  );
}

/** Type-only re-export so the test fixtures can type their CFs. */
export type { CloudFunction };
